import { fetchRfiService as fetchWithToken } from '../utils/fetchRfiService';
import { getApiBaseUrl } from '../config';
import { headers, HttpMethod } from '../constants';
import {
  CustomAttribute,
  NewCustomAttribute,
  GetCustomAttributesRequest,
  GetCustomAttributesResponse,
  UpdateCustomAttributeRequest,
} from '../types';
import { CreateSDKConfig } from './types';
import {
  CustomAttributeMigration,
  InternalCustomAttribute,
  InternalGetCustomAttributesResponse,
  InternalUpdateAttributeRequestDefinition,
} from '../utils/migrationHelpers/CustomAttributeMigration';
import { getFlatParams } from '../utils/fetchHelpers';

const CustomAttributeApi = ({ projectId }: CreateSDKConfig) => {
  const url = `${getApiBaseUrl(projectId)}/attributes`;
  return {
    async getAll(
      getAllRequest: GetCustomAttributesRequest = {},
    ): Promise<GetCustomAttributesResponse> {
      const { limit, offset, filterStatus = ['active', 'inactive'] } = getAllRequest;
      const searchParams = new URLSearchParams();
      if (limit) {
        searchParams.set('limit', limit.toString());
      }
      if (offset) {
        searchParams.set('offset', offset.toString());
      }
      if (filterStatus?.length) {
        searchParams.set('filter[status]', getFlatParams(filterStatus));
      }
      const search = searchParams.toString();
      const { pagination, results: serverCustomAttributesResponse } =
        await fetchWithToken<InternalGetCustomAttributesResponse>(
          search ? `${url}?${search}` : url,
        );

      const customAttributes = CustomAttributeMigration.convertToCustomAttributes(
        serverCustomAttributesResponse,
      );
      return {
        pagination,
        results: customAttributes,
      };
    },

    async create(newCustomAttribute: NewCustomAttribute): Promise<CustomAttribute> {
      const body = CustomAttributeMigration.buildCreateRequest(newCustomAttribute);
      const serverCustomAttributeResponse = await fetchWithToken<InternalCustomAttribute>(url, {
        method: HttpMethod.Post,
        body: JSON.stringify(body),
        headers,
      });
      const createdCustomAttribute = CustomAttributeMigration.convertToCustomAttribute(
        serverCustomAttributeResponse,
      );
      return createdCustomAttribute;
    },
    async update(
      id: string,
      updateCustomAttributeRequest: UpdateCustomAttributeRequest,
    ): Promise<CustomAttribute> {
      const body = CustomAttributeMigration.buildUpdateRequest(updateCustomAttributeRequest);
      const patchUrl = `${url}/${id}`;
      const serverCustomAttributeResponse = await fetchWithToken<InternalCustomAttribute>(
        patchUrl,
        {
          method: HttpMethod.Patch,
          body: JSON.stringify(body),
          headers,
        },
      );
      const updatedCustomAttribute = CustomAttributeMigration.convertToCustomAttribute(
        serverCustomAttributeResponse,
      );
      return updatedCustomAttribute;
    },
    async delete(id: string): Promise<CustomAttribute> {
      // Delete has been implemented as an hide status in the BE
      const body: InternalUpdateAttributeRequestDefinition = {
        status: 'hidden',
      };
      const patchUrl = `${url}/${id}`;
      const serverCustomAttributeResponse = await fetchWithToken<InternalCustomAttribute>(
        patchUrl,
        {
          method: HttpMethod.Patch,
          body: JSON.stringify(body),
          headers,
        },
      );
      const updatedCustomAttribute = CustomAttributeMigration.convertToCustomAttribute(
        serverCustomAttributeResponse,
      );
      return updatedCustomAttribute;
    },
  };
};

export default CustomAttributeApi;
